import {Button, Dropdown, Menu, Pagination, Table} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Icon from "../../../components/Icon/Icon";
import Spinner from '../../../components/Spinner/Spinner';
import Tag from "../../../components/Tag/Tag";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";

import { pageSize, useArchivedTab } from "./useArchivedTab";

import { FeedbackTypes, Props, Status, TableData, TabStatus, User } from "./ArchivedTab.model";

import styles from "./ArchivedTab.module.scss";
import moment from "moment";

const ArchivedTab = ({ filters }: Props) => {
	const {
		activateFeedback,
		getFeedback,
		onChangeFeedbackPagination,
		sendToSpam,
		state
	} = useArchivedTab({ filters });

	const {
		pagination,
		helpRequests,
		tabStatus
	} = state;

	const intl = useIntl();

	const history = useHistory();

	const getTagColorByStatus = (status: Status) => {
		switch (status) {
			case 'InProgress': return "grey";
			case 'Unassigned': return "text";
			case 'Completed': return "red";
		}
	}

	const columnsApplications = [
		{
			dataIndex: 'shortDescription',
			render: (shortDescription: string, record: TableData) => (<div className={styles.flexCell}>
				{getIconByFeedbackType(record.feedbackType)}
				<span>{shortDescription}</span>
			</div>),
			title: intl.formatMessage({ id: 'table.column.subject' }),
		},
		{
			dataIndex: 'createdAt',
			render: (createdAt: string) => moment(createdAt).format("DD MMM YYYY"),
			title: intl.formatMessage({ id: 'table.column.date' }),
		},
		{
			dataIndex: 'createdBy',
			render: (createdBy: User) => (<span>{!createdBy.isDeleted ? `${createdBy.firstName} ${createdBy.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}</span>),
			title: intl.formatMessage({ id: 'table.column.sender' }),
		},
		{
			render: ({assignedTo}: TableData) => assignedTo && (
				<div className={styles.flexCell}>
					<div className={styles.avatar}>
						{assignedTo?.imageId  &&  !assignedTo.isDeleted ?
							<img src={assignedTo.imageId} alt="" /> :
							<Icon
								color="light"
								height="12px"
								name="user"
								width="12px"
							/>}
					</div>
					<span>{!assignedTo.isDeleted ? `${assignedTo?.firstName} ${assignedTo?.lastName}`: intl.formatMessage({id: "userLabels.deletedUser"})}</span>
				</div>),
			title: intl.formatMessage({ id: 'table.column.assigned' }),
		},
		{
			dataIndex: 'status',
			render: (status: Status) => (
				<Tag noBackground color={getTagColorByStatus(status)}><div className={styles.flexTag}>{status === Status.Unassigned && <div className={styles.redDot} />}{intl.formatMessage({ id: `status.feedback.${status}` })}</div></Tag>
			),
			title: intl.formatMessage({ id: 'table.column.status' }),
		},
		{
			render: (record: TableData) => {
				return <Dropdown overlay={renderDropdown(record)}>
					<div className={styles.iconWrapper} ><Icon name="ellipsis" color="text" /> </div>
				</Dropdown>
			},
		},
	];

	const renderDropdown = (item: TableData) => {
		return <Menu>
			<Menu.Item key="1">
				<Button type="link" onClick={(e) => {
					e.stopPropagation();
					activateFeedback(item.id);
				}}>
					<FormattedMessage id="buttons.unarchive" /></Button>
			</Menu.Item>
			<Menu.Item key="2">
				<Button type="link" onClick={(e) => {
					e.stopPropagation();
					sendToSpam(item.id);
				}}>
					<FormattedMessage id="buttons.spam" />
				</Button>
			</Menu.Item>
		</Menu>
	}


	const getIconByFeedbackType = (feedbackType: FeedbackTypes) => {
		switch (feedbackType) {
			case "BugReport": return <><Icon name="comment-alt-edit" width="16px" height="16px" className={styles.purple} type="solid" />
				<span className={styles.bug}><FormattedMessage id="formLabels.bug" />: </span></>;
			case "FlagOfAbuse": return <><Icon name="comment-alt-edit" width="16px" height="16px" type="solid" />
				<span className={styles.report}><FormattedMessage id="formLabels.report" />: </span></>
			default: return <Icon name="comment-alt-edit" width="16px" height="16px" color="light" type="solid" />
		}
	}

	const onApplicationRow = (rowData: TableData) => {
		return {
			onClick: () => {
				history.push(`/help-requests/${rowData.id}`);
			},
		};
	}


	return <div className={styles.tabContent}>
		<Spinner spinning={tabStatus === TabStatus.Loading} />
		<ErrorMessage error={tabStatus === TabStatus.Error} onRefresh={getFeedback} showReport={false}/>
		<div className={styles.table}>
			<Table
				columns={columnsApplications}
				dataSource={helpRequests}
				pagination={false}
				onRow={onApplicationRow}
				rowKey={(record) => record.id}
			/>
		</div>
		<div className={styles.contentMobile}>
			{helpRequests &&
			helpRequests.map((request, key) => (
				<div
					className={styles.container}
					key={key}
					onClick={() =>
						history.push(
							`/help-requests/${request.id}`
						)
					}
				>
					<div className={`${styles.wrapper} ${styles.borderBottom}`}>
						<div className={styles.userInfo}>
							<div className={styles.flexCell}>
								<Icon name="comment-alt-edit" width="16px" height="16px" color="light" type="solid" />
								<span>{request.shortDescription}</span>
							</div>
						</div>
					</div>
					<div className={styles.info}>
						<div>
							<h2>
								<FormattedMessage id="table.column.date" />
							</h2>
						</div>
						<div>{moment(request.createdAt).format("DD MMM YYYY")}</div>
					</div>
					<div className={styles.info}>
						<div>
							<h2>
								<FormattedMessage id="table.column.sender" />
							</h2>
						</div>
						<span><span>{request.createdBy.firstName} {request.createdBy.lastName}</span></span>
					</div>
					<div className={styles.info}>
						<div>
							<h2>
								<FormattedMessage id="table.column.status" />
							</h2>
						</div>
						<Tag noBackground color={getTagColorByStatus(request.status)}><div className={styles.flexTag}>{request.status === Status.Unassigned && <div className={styles.redDot} />}{intl.formatMessage({ id: `status.feedback.${request.status}` })}</div></Tag>
					</div>
					<div className={styles.info}>
						{request.assignedTo && (<>
							<div>
								<h2>
									<FormattedMessage id="table.column.assigned" />
								</h2>
							</div>
							<div className={styles.flexCell}>
								<div className={styles.avatar}>
									{request.assignedTo?.imageId  &&  !request.assignedTo.isDeleted ?
										<img src={request.assignedTo.imageId} alt="" /> :
										<Icon
											color="light"
											height="12px"
											name="user"
											width="12px"
										/>}
								</div>
								<span>{!request.assignedTo.isDeleted ? `${request.assignedTo?.firstName} ${request.assignedTo?.lastName}`: intl.formatMessage({id: "userLabels.deletedUser"})}</span>
							</div>
						</>)}
					</div>
					<div className={styles.assign}>
						<Button type="link" onClick={(e) => {
							e.stopPropagation();
							activateFeedback(request.id);
						}}>
							<FormattedMessage id="buttons.unarchive" /></Button>
						<Button type="link" onClick={(e) => {
							e.stopPropagation();
							sendToSpam(request.id);
						}}>
							<FormattedMessage id="buttons.spam" />
						</Button>
					</div>
				</div>
			))}

		</div>
		<div className={styles.paginationWrapper}>
			<Pagination
				className={styles.pagination}
				current={pagination.page}
				pageSize={pageSize}
				total={pagination.total}
				onChange={onChangeFeedbackPagination}
				showSizeChanger={false}
			/>
		</div>
	</div>
}
export default ArchivedTab;